import { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { brmAPI } from "@/services/api";
import { addPendingEnrollment, isOnline as checkOnline } from "@/services/offline";
import { UserPlus, Loader2, CheckCircle, WifiOff, ArrowLeft, Megaphone } from "lucide-react";
import {
  TailAdminFormSection,
  TailAdminPanel,
} from "@/components/template/tailadmin/DashboardPrimitives";
import CampaignTypeBadge from "@/components/campaign/CampaignTypeBadge";

const IDENTIFIER_LABELS = {
  phone: "Phone Number",
  email: "Email Address",
  custom: "User Identifier",
};

const BRMEnrollUser = ({ isOnline }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const presetCampaignId = searchParams.get("campaign_id");
  const presetProductId = searchParams.get("product_id");

  const [identifier, setIdentifier] = useState("");
  const [notes, setNotes] = useState("");
  const [selectedCampaignId, setSelectedCampaignId] = useState("");
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(true);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [registrationValues, setRegistrationValues] = useState({});

  const fetchCampaigns = useCallback(async () => {
    setLoadingCampaigns(true);
    try {
      const res = await brmAPI.getJoinedCampaigns();
      const list = res.data?.campaigns || res.campaigns || [];
      setCampaigns(list);
      if (list.length > 0) {
        const match = presetCampaignId
          ? list.find((c) => c.campaign_id === presetCampaignId)
          : null;
        setSelectedCampaignId(match ? presetCampaignId : list[0].campaign_id);
      }
    } catch (error) {
      toast.error("Failed to load your campaigns");
    } finally {
      setLoadingCampaigns(false);
    }
  }, [presetCampaignId]);

  useEffect(() => {
    fetchCampaigns();
  }, [fetchCampaigns]);

  const selectedCampaign = campaigns.find((c) => c.campaign_id === selectedCampaignId);
  const registrationFields = selectedCampaign?.registration_form?.fields || [];
  const dedupeField = registrationFields.find((field) => field.dedupe);
  const isDirectV2 = selectedCampaign?.request_type === "direct" && selectedCampaign?.direct_version === "v2";
  const identifierType = isDirectV2
    ? (dedupeField?.type || "custom")
    : (selectedCampaign?.user_identifier_type || "phone");

  const formatPhone = (value) => value.replace(/\D/g, "");

  const getNormalizedIdentifier = () => {
    const sourceValue = isDirectV2
      ? String(registrationValues[dedupeField?.id] || "")
      : identifier;
    if (identifierType === "phone") {
      const cleanPhone = sourceValue.replace(/\D/g, "");
      if (cleanPhone.startsWith("0")) return `+234${cleanPhone.slice(1)}`;
      if (cleanPhone.startsWith("234")) return `+${cleanPhone}`;
      return `+234${cleanPhone}`;
    }
    if (identifierType === "email") {
      return sourceValue.trim().toLowerCase();
    }
    return sourceValue.trim();
  };

  const validateIdentifier = () => {
    if (isDirectV2 && registrationFields.some((field) => field.required && !String(registrationValues[field.id] ?? "").trim())) {
      return false;
    }
    const sourceValue = isDirectV2
      ? String(registrationValues[dedupeField?.id] || "")
      : identifier;
    if (identifierType === "phone") {
      return sourceValue.replace(/\D/g, "").length >= 10;
    }
    if (identifierType === "email") {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(sourceValue.trim());
    }
    return sourceValue.trim().length >= 3;
  };

  const handleEnroll = async () => {
    if (!validateIdentifier()) {
      toast.error(`Please enter a valid ${IDENTIFIER_LABELS[identifierType]?.toLowerCase() || "identifier"}`);
      return;
    }
    if (!selectedCampaign) {
      toast.error("Please select a campaign");
      return;
    }

    const normalizedId = getNormalizedIdentifier();
    const productId = selectedCampaign.product_id;
    const campaignId = selectedCampaign.campaign_id;
    setLoading(true);

    if (!isOnline) {
      if (isDirectV2) {
        toast.error("Direct V2 registration requires an online connection", {
          description: "Customer registration details are never stored in the offline queue.",
        });
        setLoading(false);
        return;
      }
      addPendingEnrollment({
        phone: identifierType === "phone" ? normalizedId : null,
        userIdentifier: normalizedId,
        userIdentifierType: identifierType,
        productId,
        notes: notes || null,
        campaignId,
        registrationData: isDirectV2 ? registrationValues : null,
      });
      toast.success("Enrollment queued!", { description: "Will sync when you're back online" });
      setSuccess(true);
      setTimeout(() => {
        setIdentifier("");
        setNotes("");
        setRegistrationValues({});
        setSuccess(false);
      }, 2000);
      setLoading(false);
      return;
    }

    try {
      await brmAPI.createEnrollment(
        normalizedId,
        productId,
        notes || null,
        campaignId,
        identifierType !== "phone" ? identifierType : null,
        isDirectV2 ? registrationValues : null,
      );
      toast.success("User enrolled successfully!");
      setSuccess(true);
      setTimeout(() => {
        setIdentifier("");
        setNotes("");
        setRegistrationValues({});
        setSuccess(false);
      }, 2000);
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "User already enrolled for this product") {
        toast.error("This user is already enrolled for this campaign");
      } else {
        toast.error(detail || "Failed to enroll user");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="px-4 py-6 min-h-[80vh] flex items-center justify-center">
        <div className="text-center animate-slide-up">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4 animate-pulse-glow">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h2 className="text-2xl font-semibold text-gray-900 mb-2">Enrolled!</h2>
          <p className="text-gray-500">
            {isOnline ? "User has been enrolled successfully" : "Queued for sync"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate("/brm/dashboard")} data-testid="back-btn">
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-gray-900">Enroll User</h1>
          <p className="text-sm text-gray-500">Select a campaign you've joined, then add the user</p>
        </div>
      </div>

      {!isOnline && (
        <section className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="offline-notice">
          <div className="flex items-center gap-3">
            <WifiOff className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-medium text-amber-800">You're offline</p>
              <p className="text-sm text-amber-600">Enrollment will be queued and synced later</p>
            </div>
          </div>
        </section>
      )}

      <TailAdminFormSection
        icon={Megaphone}
        title="Select Campaign"
        description="Each Prove-Request you joined has its own enrollment rules."
      >
        {loadingCampaigns ? (
          <Skeleton className="h-10 w-full" />
        ) : campaigns.length === 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-500">
              You haven't joined any face-to-face campaigns yet.
            </p>
            <Button variant="outline" onClick={() => navigate("/brm/campaigns")}>
              Browse Campaign Marketplace
            </Button>
          </div>
        ) : (
          <Select value={selectedCampaignId} onValueChange={setSelectedCampaignId}>
            <SelectTrigger data-testid="campaign-select" className="border-gray-200 bg-white focus:border-brand-400">
              <SelectValue placeholder="Select a campaign" />
            </SelectTrigger>
            <SelectContent>
              {campaigns.map((campaign) => (
                <SelectItem key={campaign.campaign_id} value={campaign.campaign_id}>
                  <div className="flex flex-col">
                    <span>{campaign.title}</span>
                    <span className="text-xs text-gray-500">
                      {campaign.campaign_id.slice(0, 8)}… · ₦{campaign.commission_per_goal?.toLocaleString()} / goal
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {selectedCampaign && (
          <div className="mt-3 rounded-xl border border-gray-100 bg-gray-50 p-3 text-sm">
            <p className="font-medium text-gray-900">{selectedCampaign.title}</p>
            <p className="mt-1 text-gray-500">{selectedCampaign.product_name}</p>
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs">
              <CampaignTypeBadge
                requestType={selectedCampaign.request_type}
                directVersion={selectedCampaign.direct_version}
              />
              <span className="text-emerald-600 font-medium">
                ₦{selectedCampaign.commission_per_goal?.toLocaleString()} per conversion
              </span>
              <span className="text-gray-500">
                ID type: {selectedCampaign.user_identifier_type}
              </span>
              <span className="text-gray-500 capitalize">
                Status: {selectedCampaign.participation_status}
              </span>
            </div>
          </div>
        )}
      </TailAdminFormSection>

      {selectedCampaign && (
        <TailAdminFormSection
          icon={UserPlus}
          title="User Details"
          description={`Enter the ${IDENTIFIER_LABELS[identifierType]?.toLowerCase() || "identifier"} for this campaign.`}
        >
          <div className="space-y-4">
            {isDirectV2 ? (
              <div className="space-y-4">
                <div className="rounded-xl border border-brand-200 bg-brand-50 p-3 text-sm text-gray-700">
                  Complete the founder's registration form. Proovam does not request passwords.
                </div>
                {registrationFields.map((field) => (
                  <div key={field.id} className="space-y-2">
                    <Label htmlFor={`registration-${field.id}`}>
                      {field.label}{field.required ? " *" : ""}
                    </Label>
                    {field.type === "hidden" ? (
                      <p className="text-sm text-gray-500">Provided automatically by the founder.</p>
                    ) : field.type === "consent" ? (
                      <input
                        id={`registration-${field.id}`}
                        type="checkbox"
                        checked={Boolean(registrationValues[field.id])}
                        onChange={(event) => setRegistrationValues((current) => ({ ...current, [field.id]: event.target.checked }))}
                      />
                    ) : field.type === "select" ? (
                      <Select
                        value={registrationValues[field.id] || ""}
                        onValueChange={(value) => setRegistrationValues((current) => ({ ...current, [field.id]: value }))}
                      >
                        <SelectTrigger id={`registration-${field.id}`}><SelectValue placeholder="Select an option" /></SelectTrigger>
                        <SelectContent>
                          {(field.options || []).map((option) => <SelectItem key={option} value={option}>{option}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        id={`registration-${field.id}`}
                        type={field.type === "phone" ? "tel" : field.type}
                        value={registrationValues[field.id] || ""}
                        onChange={(event) => setRegistrationValues((current) => ({ ...current, [field.id]: event.target.value }))}
                      />
                    )}
                    {field.dedupe && (
                      <p className="text-xs text-brand-500">Used only for safe local duplicate detection.</p>
                    )}
                  </div>
                ))}
              </div>
            ) : <div className="space-y-2">
              <Label htmlFor="identifier" className="text-sm font-medium text-gray-700">
                {IDENTIFIER_LABELS[identifierType] || "Identifier"} *
              </Label>
              {identifierType === "phone" ? (
                <div className="phone-input-group">
                  <span className="phone-input-prefix">+234</span>
                  <input
                    id="identifier"
                    type="tel"
                    placeholder="8012345678"
                    value={identifier}
                    onChange={(e) => setIdentifier(formatPhone(e.target.value))}
                    className="phone-input-field"
                    maxLength={11}
                    data-testid="enroll-phone-input"
                  />
                </div>
              ) : (
                <Input
                  id="identifier"
                  type={identifierType === "email" ? "email" : "text"}
                  placeholder={
                    identifierType === "email"
                      ? "user@example.com"
                      : selectedCampaign.user_identifier_field || "User ID"
                  }
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  data-testid="enroll-identifier-input"
                />
              )}
            </div>}

            <div className="space-y-2">
              <Label htmlFor="notes" className="text-sm font-medium text-gray-700">Notes (Optional)</Label>
              <Textarea
                id="notes"
                placeholder="Any additional information about this user..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="border-gray-200 focus:border-brand-400"
                data-testid="enroll-notes-input"
              />
            </div>

            <Button
              className="w-full h-12"
              onClick={handleEnroll}
              disabled={loading || !validateIdentifier() || !selectedCampaignId || loadingCampaigns}
              data-testid="enroll-submit-btn"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              {isOnline ? "Enroll User" : "Queue Enrollment"}
            </Button>
          </div>
        </TailAdminFormSection>
      )}

      <TailAdminPanel title="Tips" action={null}>
        <ul className="space-y-2 text-sm text-gray-500">
          <li>Pick the correct campaign — each has a unique ID and commission rate.</li>
          <li>Enrollment fields follow the campaign's identifier type (phone, email, or custom).</li>
          <li>You'll earn commission when the user converts and verification passes.</li>
          <li>Works offline and syncs when back online.</li>
        </ul>
      </TailAdminPanel>
    </div>
  );
};

export default BRMEnrollUser;
